<script setup lang="ts">
import { ref } from 'vue';
import { useRouter, RouterLink } from 'vue-router';
import { signUp, signIn, authClient } from '../auth';
import AppHeader from '@/components/layout/AppHeader.vue';
import { Button } from '@/components/ui/button';

const router = useRouter();
const name = ref('');
const email = ref('');
const password = ref('');
const error = ref('');
const loading = ref(false);

async function handleSubmit() {
  error.value = '';
  loading.value = true;

  try {
    const result = await signUp.email({
      email: email.value,
      password: password.value,
      name: name.value.trim() || '',
    });

    if (result.error) {
      error.value = result.error.message || 'Sign up failed';
      return;
    }

    // Verify session was established (auto-login after signup)
    let session = await authClient.getSession();

    // If no session, explicitly sign in with the same credentials
    if (!session.data?.user) {
      const signInResult = await signIn.email({
        email: email.value,
        password: password.value,
      });

      if (signInResult.error) {
        error.value = 'Account created but auto-login failed. Please sign in.';
        router.push('/sign-in');
        return;
      }

      session = await authClient.getSession();
      if (!session.data?.user) {
        error.value = 'Account created but session was not established. Please sign in.';
        router.push('/sign-in');
        return;
      }
    }

    router.push('/learn');
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'An error occurred';
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="min-h-screen bg-background">
    <AppHeader />

    <main class="max-w-md mx-auto px-4 py-12">
      <div class="bg-card rounded-lg shadow-md p-8 border border-border">
        <h1 class="text-2xl font-bold text-center mb-6 text-foreground">
          Sign Up
        </h1>

        <form
          class="space-y-4"
          @submit.prevent="handleSubmit"
        >
          <div>
            <label
              for="name"
              class="block text-sm font-medium text-foreground mb-1"
            >
              Name (optional)
            </label>
            <input
              id="name"
              v-model="name"
              type="text"
              class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
          </div>

          <div>
            <label
              for="email"
              class="block text-sm font-medium text-foreground mb-1"
            >
              Email
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
          </div>

          <div>
            <label
              for="password"
              class="block text-sm font-medium text-foreground mb-1"
            >
              Password
            </label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              minlength="8"
              class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
          </div>

          <div
            v-if="error"
            class="text-destructive text-sm"
          >
            {{ error }}
          </div>

          <Button
            type="submit"
            :disabled="loading"
            class="w-full"
          >
            {{ loading ? 'Creating account...' : 'Sign Up' }}
          </Button>

          <p class="text-xs text-muted-foreground text-center">
            By signing up, you agree to our
            <RouterLink
              to="/privacy"
              class="text-primary hover:underline"
            >
              Privacy Policy
            </RouterLink>
          </p>
        </form>

        <p class="mt-4 text-center text-sm text-muted-foreground">
          Already have an account?
          <RouterLink
            to="/sign-in"
            class="text-primary hover:underline"
          >
            Sign in
          </RouterLink>
        </p>
      </div>
    </main>
  </div>
</template>
